import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  ClockCounterClockwise,
  ArrowsClockwise,
  Receipt,
  CreditCard,
  User,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/lib/api";

const money = (n) =>
  `$${Number(n || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const when = (iso) =>
  iso
    ? new Date(iso).toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : "—";

export const QboHistory = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("invoices");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/qbo/history");
      setData(d);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not load sync history",
      );
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (!data) return null;

  const tabs = [
    ["invoices", "Invoices", Receipt, data.counts?.invoices || 0],
    ["payments", "Payments", CreditCard, data.counts?.payments || 0],
    ["customers", "Customers", User, data.counts?.customers || 0],
  ];
  const rows = data[tab] || [];

  return (
    <div
      className="mt-6 rounded-2xl border border-zinc-800 bg-zinc-900 p-6"
      data-testid="qbo-history-panel"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <ClockCounterClockwise
            size={18}
            weight="bold"
            className="text-indigo-300"
          />
          <div>
            <h3 className="text-base font-semibold text-zinc-100">
              Sync history
            </h3>
            <p className="text-sm text-zinc-500">
              Everything this app has pushed into QuickBooks.
            </p>
          </div>
        </div>
        <Button
          data-testid="qbo-history-refresh"
          variant="outline"
          onClick={load}
          disabled={loading}
          className="gap-2 border-zinc-700 bg-transparent text-zinc-200 hover:bg-zinc-800"
        >
          <ArrowsClockwise size={15} weight="bold" />
          {loading ? "Refreshing…" : "Refresh"}
        </Button>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {tabs.map(([key, label, Icon, count]) => (
          <button
            key={key}
            type="button"
            data-testid={`qbo-history-tab-${key}`}
            onClick={() => setTab(key)}
            className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold uppercase tracking-widest transition-colors duration-150 ${
              tab === key
                ? "bg-indigo-500 text-white"
                : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700"
            }`}
          >
            <Icon size={13} weight="bold" /> {label}
            <span className="rounded-full bg-black/20 px-1.5">{count}</span>
          </button>
        ))}
      </div>

      <div
        className="mt-4 max-h-80 overflow-y-auto rounded-xl border border-zinc-800"
        data-testid="qbo-history-list"
      >
        {rows.length === 0 ? (
          <p
            className="p-6 text-center text-sm text-zinc-600"
            data-testid="qbo-history-empty"
          >
            Nothing synced yet — new {tab} will appear here as they post to
            QuickBooks.
          </p>
        ) : (
          <table className="w-full text-sm">
            <tbody>
              {rows.map((r, i) => (
                <tr
                  key={`${tab}-${i}`}
                  data-testid={`qbo-history-row-${tab}-${i}`}
                  className="border-b border-zinc-800/70 last:border-0"
                >
                  <td className="px-4 py-2.5">
                    <span className="font-mono-data text-indigo-300">
                      {tab === "customers" ? r.name || "—" : r.code || "—"}
                    </span>
                    {tab !== "customers" && r.customer_name && (
                      <span className="ml-2 text-zinc-400">
                        {r.customer_name}
                      </span>
                    )}
                    {tab === "invoices" && r.kind && (
                      <span className="ml-2 text-[10px] uppercase tracking-wider text-zinc-600">
                        {r.kind}
                      </span>
                    )}
                    {tab === "payments" && r.method && (
                      <span className="ml-2 text-zinc-600">{r.method}</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right text-zinc-400">
                    {tab !== "customers" && r.amount ? money(r.amount) : ""}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-right text-zinc-500">
                    QB #{r.qbo_id}
                  </td>
                  {tab !== "customers" && (
                    <td className="whitespace-nowrap px-4 py-2.5 text-right text-xs text-zinc-600">
                      {when(r.at)}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};
